import { useCallback } from "react";
import { useQuizNavigation, type QuizResponse, type QuizFormData } from "@core/features/quiz";
import {
  useUpdateUserAffiliations,
  orgRoleEntriesToAffiliationInputs,
  type AffiliationEntry,
} from "@core/features/affiliations";

type Args = {
  quiz: QuizResponse | undefined;
  quizResponseId: number | undefined;
  resetIndexKey?: unknown;
  onCompleteQuiz: (formData: QuizFormData) => void;
};

/**
 * Platform-specific wrapper around useQuizNavigation.
 * Adds UI-specific behavior like dialog focus management and affiliation saving.
 */
export function useQuizProps({
  quiz,
  quizResponseId,
  resetIndexKey,
  onCompleteQuiz,
}: Args) {
  const { updateAffiliationsAsync } = useUpdateUserAffiliations();

  const handleSaveAffiliations = useCallback(
    async (entries: unknown[]): Promise<number[]> => {
      const inputs = orgRoleEntriesToAffiliationInputs(entries as AffiliationEntry[]);
      const saved = await updateAffiliationsAsync(inputs);
      return saved.map((a) => a.id);
    },
    [updateAffiliationsAsync]
  );

  const result = useQuizNavigation({
    quiz,
    quizResponseId,
    resetIndexKey,
    onCompleteQuiz,
    onSaveAffiliations: handleSaveAffiliations,
    // Platform-specific: refocus on dialog heading after submission
    onAnswerSubmitted: () => {
      const dialogHeading =
        document.querySelector<HTMLDivElement>("#dialog-title");
      if (dialogHeading) {
        dialogHeading.focus();
      }
    },
  });

  return result;
}

export default useQuizProps;


